import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ShopLayout from './ShopLayout.js';
import { useCart } from '../../contexts/CartContext.js';

const FLARE_RED = '#c4151c';

const Icon = ({ path, className = "h-5 w-5" }) => <svg xmlns="http://www.w3.org/2000/svg" className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} /></svg>;

const formatPKR = (n) => `Rs ${(Number(n) || 0).toLocaleString('en-PK')}`;

export default function CartReviewSection() {
  const navigate = useNavigate();
  const {
    items,
    removeItem,
    updateQuantity,
    unitPrice,
    lineTotal,
    hasPhysical,
    subtotal,
    deliveryCharges,
    total,
    clearCart,
  } = useCart();

  if (items.length === 0) {
    return (
      <ShopLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-24 h-24 mx-auto mb-6 rounded-3xl flex items-center justify-center bg-gray-50 border border-gray-200">
            <Icon path="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" className="h-12 w-12 text-gray-300" />
          </div>
          <h1 className="text-3xl font-black text-gray-900 mb-3">Your cart is empty</h1>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">Add magazines from our shop to get started. Choose instant digital downloads or printed copies delivered nationwide.</p>
          <Link
            to="/magazines"
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold shadow-md"
            style={{ backgroundColor: FLARE_RED }}
          >
            <Icon path="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" className="h-5 w-5" />
            Browse Magazines
          </Link>
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <div className="mb-8">
          <Link to="/magazines" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
            <Icon path="M10 19l-7-7m0 0l7-7m-7 7h18" className="h-4 w-4" />
            Continue shopping
          </Link>
          <h1 className="mt-4 text-3xl sm:text-4xl font-black tracking-tight text-gray-900">Your Shopping Cart</h1>
          <p className="mt-1 text-gray-600">{items.length} item{items.length === 1 ? '' : 's'} in your cart.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => {
              const snap = item.snapshot || {};
              const up = unitPrice(item);
              const lt = lineTotal(item);
              return (
                <div key={item.cartId} className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4 sm:p-5 flex gap-4 sm:gap-5 flex-wrap sm:flex-nowrap">
                  <Link to={`/magazines/${snap.slug || ''}`} className="shrink-0 w-24 sm:w-32 aspect-[3/4] rounded-xl overflow-hidden bg-gray-50 border border-gray-100">
                    {snap.cover_image_url ? (
                      <img src={snap.cover_image_url} alt={snap.title || snap.month_year} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = '0.2'; }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-400 p-2 text-center">{snap.title || snap.month_year || 'Issue'}</div>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0 flex flex-col">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider mb-1.5 ${item.delivery_type === 'Digital' ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' : 'bg-emerald-50 text-emerald-800 border border-emerald-100'}`}>
                          {item.delivery_type === 'Digital' ? (
                            <><Icon path="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" className="h-3 w-3" /> Digital</>
                          ) : (
                            <><Icon path="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" className="h-3 w-3" /> Physical</>
                          )}
                        </span>
                        <Link to={`/magazines/${snap.slug || ''}`} className="block font-extrabold text-gray-900 text-lg leading-tight hover:underline line-clamp-2">
                          {snap.title || snap.month_year || 'Flare Magazine Issue'}
                        </Link>
                        <div className="mt-0.5 text-xs font-semibold uppercase tracking-wider" style={{ color: FLARE_RED }}>
                          {snap.month_year}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Unit: {formatPKR(up)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-xs font-bold uppercase tracking-widest text-gray-400">Line total</div>
                        <div className="text-xl font-black" style={{ color: FLARE_RED }}>{formatPKR(lt)}</div>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-4 justify-between">
                      <div className="flex items-center border border-gray-200 rounded-xl bg-white overflow-hidden">
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.cartId, (item.quantity || 1) - 1)}
                          className="w-10 h-10 text-gray-600 hover:bg-gray-50 font-extrabold text-lg"
                          aria-label="Decrease quantity"
                        >−</button>
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={e => updateQuantity(item.cartId, e.target.value)}
                          className="w-16 h-10 text-center text-sm font-black bg-transparent focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                        />
                        <button
                          type="button"
                          onClick={() => updateQuantity(item.cartId, (item.quantity || 1) + 1)}
                          className="w-10 h-10 text-gray-600 hover:bg-gray-50 font-extrabold text-lg"
                          aria-label="Increase quantity"
                        >+</button>
                      </div>
                      <button
                        onClick={() => removeItem(item.cartId)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-bold text-rose-700 bg-rose-50 border border-rose-100 hover:bg-rose-100 transition-colors"
                      >
                        <Icon path="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" className="h-4 w-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="pt-2 flex items-center justify-between">
              <button onClick={() => clearCart()} className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-800 transition-colors">
                <Icon path="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" className="h-4 w-4" />
                Clear entire cart
              </button>
            </div>
          </div>

          <aside className="lg:sticky lg:top-24 space-y-5">
            <div className="rounded-3xl bg-white border border-gray-100 shadow-sm p-6">
              <h2 className="text-lg font-extrabold text-gray-900 mb-5 flex items-center gap-2">
                <Icon path="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" className="h-5 w-5" style={{ color: FLARE_RED }} />
                Order Summary
              </h2>
              <dl className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <dt className="font-semibold text-gray-600">Subtotal</dt>
                  <dd className="font-bold text-gray-900">{formatPKR(subtotal)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="font-semibold text-gray-600">
                    {hasPhysical ? 'Delivery (Pakistan)' : 'Delivery (Digital)'}
                  </dt>
                  <dd className="font-bold text-gray-900">
                    {hasPhysical ? (deliveryCharges > 0 ? formatPKR(deliveryCharges) : 'TBD at checkout') : 'Free'}
                  </dd>
                </div>
                <div className="border-t border-gray-100 my-3" />
                <div className="flex items-start justify-between pt-1">
                  <dt className="font-extrabold text-gray-900 text-base">Total</dt>
                  <dd className="text-right">
                    <div className="text-2xl font-black tracking-tight" style={{ color: FLARE_RED }}>
                      {formatPKR(total)}
                    </div>
                    <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                      {items.reduce((n, i) => n + (i.quantity || 0), 0)} item(s)
                    </div>
                  </dd>
                </div>
              </dl>

              <button
                onClick={() => navigate('/checkout')}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl text-white font-extrabold shadow-lg transition-transform hover:-translate-y-0.5"
                style={{ backgroundColor: FLARE_RED, boxShadow: '0 10px 24px -10px rgba(196,21,28,0.5)' }}
              >
                Proceed to Checkout
                <Icon path="M14 5l7 7m0 0l-7 7m7-7H3" className="h-5 w-5" />
              </button>
              <p className="mt-3 text-center text-[11px] text-gray-400 font-semibold uppercase tracking-widest">
                Secure Checkout · JazzCash · EasyPaisa
              </p>
            </div>

            <div className="rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-rose-50 border border-gray-100 p-5">
              <h3 className="font-extrabold text-gray-900 mb-2 flex items-center gap-2 text-sm">
                <Icon path="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" className="h-4 w-4 text-indigo-600" />
                Why shop with Flare?
              </h3>
              <ul className="space-y-1.5 text-xs text-gray-600 font-semibold">
                <li className="flex gap-2"><Icon path="M5 13l4 4L19 7" className="h-4 w-4 text-emerald-600 shrink-0" /> Instant digital delivery</li>
                <li className="flex gap-2"><Icon path="M5 13l4 4L19 7" className="h-4 w-4 text-emerald-600 shrink-0" /> Printed copies nationwide</li>
                <li className="flex gap-2"><Icon path="M5 13l4 4L19 7" className="h-4 w-4 text-emerald-600 shrink-0" /> Verified secure payments</li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </ShopLayout>
  );
}
